import { useCallback, useEffect, useState } from "react";
import { Plug } from "lucide-react";
import type { OAuthGrant } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { showToast } from "../../lib/toast";

export default function ConnectedAppsPanel() {
  const [grants, setGrants] = useState<OAuthGrant[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.auth.oauth.listGrants();
    if (error || !data) {
      setUnavailable(true);
      setGrants([]);
      return;
    }
    setGrants(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (clientId: string) => {
    setRevoking(clientId);
    const { error } = await supabase.auth.oauth.revokeGrant({ clientId });
    setRevoking(null);
    if (error) {
      showToast("Failed to revoke access");
      return;
    }
    setGrants((g) => (g ?? []).filter((x) => x.client.id !== clientId));
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">Connected apps</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        AI apps you have authorized to manage your tasks.
      </p>

      {grants === null ? (
        <div className="text-xs text-muted-foreground">Loading...</div>
      ) : unavailable ? (
        <div className="text-xs text-muted-foreground">
          Could not load connected apps. Manage them from the Supabase dashboard (Authentication → OAuth Server).
        </div>
      ) : grants.length === 0 ? (
        <div className="text-xs text-muted-foreground">No connected apps.</div>
      ) : (
        <ul className="space-y-2">
          {grants.map((g) => (
            <li key={g.client.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-foreground truncate">{g.client.name || g.client.id}</div>
                <div className="text-xs text-muted-foreground">
                  {g.scopes.join(", ")} · since {new Date(g.granted_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => handleRevoke(g.client.id)}
                disabled={revoking === g.client.id}
                className="text-xs text-destructive hover:text-destructive/80 transition-colors"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
